import React, { useEffect, useMemo, useState } from "react";
import { getSkill } from "../../../lib/inventorySkills";
import { useSeasonStats } from "../../../lib/seasonStats";
import { computeRebounding, positionFromArchetypes, rbList, useRebounding } from "../../../lib/rebounding";
import { ReboundingStatsPanel } from "./ReboundingStatsPanel";
import { ReboundingOutcomes } from "./ReboundingOutcomes";
import { PlanLink } from "../PlanLink";
import { ItemRow } from "../ItemRow";

export const EMPTY_RB = { outcome_tags: [], flags: [], checkpoints: [], limiting_factors: [], position: null };
const FROM = { "TAC-ORB-01": "Tactical", "PHY-AT-04": "Physical" };

// Rebounding = same 4 sections as Finishing: stats panel (tiers by position) → rate the 4 interactions (+2 linked) → outcome tags (3 rows) → the plan.
export function ReboundingCategory({ playerId, items, byId, rebounding, onReboundingChange, archetypes, rowProps, readOnly }) {
  const lib = useRebounding();
  const { records } = useSeasonStats(playerId);
  const rb = rebounding || EMPTY_RB;
  const position = positionFromArchetypes(archetypes);
  const [season, setSeason] = useState(rb.season || null);
  useEffect(() => { if (!season && records.length) setSeason(rb.season && records.some(r => r.season === rb.season) ? rb.season : records[0].season); }, [records, season, rb.season]);
  const rec = records.find(r => r.season === season);
  const result = useMemo(() => computeRebounding(rec?.stats, position), [rec, position]);
  useEffect(() => {
    if (readOnly || !season) return;
    const flags = result?.flags || [];
    if (season !== rb.season || position !== (rb.position || null) || JSON.stringify(flags) !== JSON.stringify(rb.flags || [])) onReboundingChange(s => ({ ...(s || EMPTY_RB), season, position, flags }));
  }, [season, result, position]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lib) return <div className="text-[10px] text-[#757575]">Loading Rebounding…</div>;
  const order = rbList(lib, "Interaction").map(r => r.id);
  const ordered = [...items].sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));
  const linked = rbList(lib, "Linked Skill").map(r => getSkill(r.id)).filter(s => s && s.status === "active");
  const look = new Set((result?.flags || []).flatMap(f => rbList(lib, "Stat Flag").find(x => x.id === f)?.points_to || []));
  const row = (s, fromCat) => (
    <div key={s.id} className="relative" data-testid={`rb-skill-${s.id}`}>
      {(look.has(s.id) || fromCat) && <div className="absolute right-3 -top-2 z-10 flex items-center gap-1.5">
        {look.has(s.id) && <span className="px-1.5 py-0.5 bg-[#f59e0b] text-[#121212] text-[8px] uppercase tracking-[0.2em] font-bold" data-testid={`rb-look-${s.id}`}>Look at this</span>}
        {fromCat && <span className="px-1.5 py-0.5 border border-white/20 bg-[#1c1c1c] text-[#a0a0a0] text-[8px] uppercase tracking-[0.2em]" data-testid={`rb-linked-${s.id}`}>From {fromCat}</span>}</div>}
      <ItemRow skill={s} rating={byId[s.id]} readOnly={readOnly} {...rowProps(s)} />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="rb-category">
      <ReboundingStatsPanel playerId={playerId} lib={lib} records={records} season={season} setSeason={setSeason} result={result} position={position} />
      <div className="border border-white/10 bg-[#141414] p-4 space-y-2" data-testid="rb-ratings">
        <div className="flex items-center gap-3 flex-wrap"><span className="text-[9px] font-mono text-[var(--org-accent)]">2</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Rate the interactions</div><div className="text-[10px] text-[#757575]">Anchor / Good / Rate Limiter · the stats inform, the coach rates</div></div>
        <div className="space-y-3 pt-1">{ordered.map(s => row(s, null))}{linked.map(s => row(s, FROM[s.id] || s.category))}</div>
      </div>
      <ReboundingOutcomes lib={lib} rb={rb} byId={byId} readOnly={readOnly} onChange={onReboundingChange} />
      <PlanLink playerId={playerId} sectionKey="rebounding" />
    </div>
  );
}
